/**
 * ChartTooltipsHelper
 *
 * Chart-icon hover tooltips and the chart availability rules behind them.
 *
 * `updateChartDisablingFlags` decides, for a given chat block (or the component
 * itself when no block is supplied), whether the table can be rendered as a chart
 * at all (`isChartView`) and which chart types must be greyed out
 * (`disableStackChart` / `disableLineChart`), plus the significance flags
 * `isDisablePairWise` / `isDisableReference`.
 *
 * NOTE: every method here keeps the original `const target = block || comp;`
 * idiom - when no block is passed the flags are written onto the component
 * itself, which is why `comp` (not a block) is the fallback target.
 *
 * Extracted verbatim from chatbotexpand.component.ts (`this.` -> `comp.`);
 * behavior is unchanged.
 */
export class ChartTooltipsHelper {

  static onChartIconMouseOver(comp: any, tooltipType: 'stack' | 'line' | 'bar', event: MouseEvent, block: any = null): void {
    const target = block || comp;
    comp.tooltipStates[tooltipType] = true;
    comp.updateChartDisablingFlags(block);
    ChartTooltipsHelper.updateTooltipFlags(comp, block);

    if (!target.isIndex && target.isChartView) {
      target.showMainTooltip = false;
      event.stopPropagation();
    } else {
      target.showMainTooltip = true;
    }
  }

  static onChartIconMouseLeave(comp: any, tooltipType: 'stack' | 'line' | 'bar', block: any = null): void {
    const target = block || comp;
    comp.tooltipStates[tooltipType] = false;
    ChartTooltipsHelper.updateTooltipFlags(comp, block);
  }

  private static updateTooltipFlags(comp: any, block: any = null): void {
    const target = block || comp;
    target.showStackTooltip = comp.tooltipStates.stack;
    target.showLineTooltip = comp.tooltipStates.line;
    target.showBarTooltip = comp.tooltipStates.bar;
  }

  static updateChartDisablingFlags(comp: any, block: any = null): void {
    const target = block || comp;
    const mcode = comp.getMcode(target);
    const blockData = target.messageId ? comp.getOutputData(target) : (comp.OutputdataObject?.Row?.length > 0 ? comp.OutputdataObject : comp.dataObject);
    const blockDataObject = target.dataObject || comp.dataObject;

    let rowdata: any[] = (blockData?.Row || []) as any[];
    let columndata: any[] = (blockData?.Column || []) as any[];
    let rowNestData = blockData?.RowNesting || blockDataObject?.RowNesting || [];
    let colNestData = blockData?.ColNesting || blockDataObject?.ColNesting || [];

    if (target.isTranspose) {
      const tempRow = rowdata;
      rowdata = columndata;
      columndata = tempRow;

      const tempNest = rowNestData;
      rowNestData = colNestData;
      colNestData = tempNest;
    }

    const rowDataLength: number = rowdata.length > 0 ? (rowdata[0].Data?.length || 0) : 0;

    const colDataLength: number = columndata.length;
    const colNestLength: number = colNestData.length;
    const singleColChildLength: number = (columndata && columndata[0]?.Data?.length) || 0;

    const isPairwiseAllowed = colDataLength === 1 && colNestLength === 0 && singleColChildLength >= 2 && singleColChildLength <= 10;
    target.isDisablePairWise = !isPairwiseAllowed;
    target.isDisableReference = colDataLength > 1;

    // 1. isChartView Logic
    const hasDataSelected = !target.DefaultDataDropDown || target.DefaultDataDropDown === 'Data';
    const hasRowNestedArray = rowdata.length > 0 && rowdata[0]?.Data?.length > 0 && (rowdata[0].Data[0].NestedArray?.length > 0);
    const hasColNestedArray = columndata.length > 0 && columndata[0]?.Data?.length > 0 && (columndata[0].Data[0].NestedArray?.length > 0);

    target.isChartView = (
      rowNestData.length > 0 ||
      colNestData.length > 0 ||
      hasRowNestedArray ||
      hasColNestedArray ||
      rowdata.length < 1 ||
      rowDataLength > comp.maxColumnsPerSlide ||
      target.isIndex ||
      !hasDataSelected
    ) ? false : true;


    if (target.isChartView) {
      const isMultiPunchRow = comp.filterDataByCalculationType(rowdata as any, 'multipunch');

      const hasMean = rowdata.some((parent: any) =>
        (parent.Data || []).some((child: any) => child.CalculationType?.toLowerCase() === 'mean')
      );
      const hasPercentage = rowdata.some((parent: any) =>
        (parent.Data || []).some((child: any) =>
          ['share', 'respgrp', 'percentage'].includes(child.CalculationType?.toLowerCase())
        )
      );

      // Disable all charts if mixed Mean and Percentage metrics are present
      if (hasMean && hasPercentage) {
        target.isChartView = false;
        target.disableStackChart = true;
        target.disableLineChart = true;
        return;
      }

      const isShareMetric = rowdata.filter((parent: any) => {
        return (parent.Data || []).some((child: any) => ['share', 'respgrp', 'percentage'].includes(child.CalculationType?.toLowerCase()));
      });

      const isMeanMetric = rowdata.filter((parent: any) => {
        return (parent.Data || []).some((child: any) => child.CalculationType?.toLowerCase() === 'mean');
      });

      const isDefaultRespGrp = rowdata.filter((parent: any) => {
        return (parent.Data || []).some((child: any) => child.CalculationType?.toLowerCase() === 'respgrp' && child.isDerviedVariable === true);
      });

      // 2. disableLineChart Logic
      const TimePeriodinColumn = columndata.filter((parent: any) =>
        (parent.Data || []).some((child: any) => child.isTimePeriod === true || child.isTimePeriod === 'true') ||
        parent.Parent?.toLowerCase().includes('time period') ||
        parent.Parent?.toLowerCase().includes('year') ||
        parent.Parent?.toLowerCase().includes('month') ||
        parent.Parent?.toLowerCase().includes('quarter') ||
        parent.Parent?.toLowerCase().includes('wave') ||
        parent.Parent?.toLowerCase().includes('mma')
      );

      const TimePeriodinRow = rowdata.filter((parent: any) =>
        (parent.Data || []).some((child: any) => child.isTimePeriod === true || child.isTimePeriod === 'true') ||
        parent.Parent?.toLowerCase().includes('time period') ||
        parent.Parent?.toLowerCase().includes('year') ||
        parent.Parent?.toLowerCase().includes('month') ||
        parent.Parent?.toLowerCase().includes('quarter') ||
        parent.Parent?.toLowerCase().includes('wave') ||
        parent.Parent?.toLowerCase().includes('mma')
      );

      const rowDataLengthForTrend = comp.getDistinctChildValues(rowdata as any);

      // 3. disableStackChart Logic
      if (rowdata.length === 1) {
        let filteredData: any = [];
        const disctinctRowValues = comp.getDistinctChildValues(rowdata as any);
        const pipeParts = comp.getDistinctPipeParts(disctinctRowValues);
        target.StackFlag = comp.calculateStackFlag(pipeParts);

        if (target.StackFlag) {
          rowdata[0].Data.forEach((item: any) => {
            const pipeCount = (item.Child.match(/\|/g) || []).length;
            if (pipeCount === 1) {
              const [level1] = item.Child.split('|');
              filteredData = comp.variableTabData.filter(
                (x: any) => x.Level1 === level1 && x.Header == 'VariableList'
              );
            } else if (pipeCount === 2) {
              const [level1, level2] = item.Child.split('|');
              filteredData = comp.variableTabData.filter(
                (x: any) =>
                  x.Level1 === level1 &&
                  x.Level2 === level2 &&
                  x.Header == 'VariableList'
              );
            } else if (pipeCount === 3) {
              const [level1, level2, level3] = item.Child.split('|');
              filteredData = comp.variableTabData.filter(
                (x: any) =>
                  x.Level1 === level1 &&
                  x.Level2 === level2 &&
                  x.level3 === level3 &&
                  x.Header == 'VariableList'
              );
            } else if (pipeCount === 4) {
              const [level1, level2, level3, level4] = item.Child.split('|');
              filteredData = comp.variableTabData.filter(
                (x: any) =>
                  x.Level1 === level1 &&
                  x.Level2 === level2 &&
                  x.level3 === level3 &&
                  x.level4 == level4 &&
                  x.Header == 'VariableList'
              );
            } else if (pipeCount === 5) {
              const [level1, level2, level3, level4, level5] = item.Child.split('|');
              filteredData = comp.variableTabData.filter(
                (x: any) =>
                  x.Level1 === level1 &&
                  x.Level2 === level2 &&
                  x.level3 === level3 &&
                  x.level4 == level4 &&
                  x.level5 == level5 &&
                  x.Header == 'VariableList'
              );
            }
          });
        } else {
          target.StackFlag = false;
        }

        let isStackChartAvailable = false;
        if (isMultiPunchRow.length > 0 || isDefaultRespGrp.length > 0 || isMeanMetric.length > 0) {
          isStackChartAvailable = false;
        } else {
          isStackChartAvailable = rowdata[0].Data.length === filteredData.length;
        }

        target.disableStackChart = !isStackChartAvailable;
      } else {
        target.disableStackChart = true; // Multiple metrics selected
      }

      // disableLineChart Logic specialized by Mcode (matching parent components)
      if (target.isTranspose) {
        target.disableLineChart = true;
      } else {
        const activeTimePeriod = TimePeriodinColumn; // When not transposed, time period is in columns
        if (mcode === 2) {
          // Brand Analysis Line Chart condition: purely based on time period in columns
          const isLineChartAvailable = activeTimePeriod.length === 1;
          target.disableLineChart = !isLineChartAvailable;
        } else {
          // Crosstab (mcode 3) Line Chart condition
          const isLineChartAvailable = activeTimePeriod.length === 1 &&
            ((isShareMetric.length > 0 || isMeanMetric.length > 0) && rowDataLengthForTrend.length <= comp.maxRowsPerSlide);
          target.disableLineChart = !isLineChartAvailable;
        }
      }

      // Additional safety: if more than 15 rows (standard chart limit), disable stack & line chart
      if (rowDataLength > comp.maxRowsPerSlide) {
        target.disableStackChart = true;
        target.disableLineChart = true;
      }
    } else {
      target.disableStackChart = true;
      target.disableLineChart = true;
    }
  }
}
